import { Checkbox, TableCell, TableRow } from '@bpinternal/ui-kit-next'
import { useNavigate } from '@tanstack/react-router'
import { Loader } from 'lucide-react'
import { DateTime } from 'luxon'
import { useEffect } from 'react'
import { Badge, Spinner } from '~/elements'
import { useWebPageSource } from '~/features/knowledge-bases/hooks/useKnowledgeSource'
import { useWebSourceCrawl } from '~/features/knowledge-bases/hooks/useWebSourceCrawl'
import { cn, formatters } from '~/utils'
import type { KnowledgeSourceStatusType, KnowledgeSourceTableRow } from '../../types'
import {
  KnowledgeSourceCrawlStatus,
  KnowledgeSourceIcon,
  KnowledgeSourceOrigin,
  KnowledgeSourceStatus,
} from './KnowledgeSourceCell'
import { useKnowledgeSourceSelection } from './KnowledgeSourceSelectionContext'

type WebPageSourceRowProps = KnowledgeSourceTableRow & {
  dsId: string
  workspaceId: string
  botId: string
  kbId: string
  backendKbId?: string
}

export const WebPageSourceRow = ({ index, dsId, workspaceId, botId, kbId, backendKbId }: WebPageSourceRowProps) => {
  const { data, fetchNextPage, hasNextPage, isFetching, title } = useWebPageSource({
    workspaceId,
    botId,
    kbId,
    dsId,
  })
  const { selectedKnowledgeSources, setSelectedKnowledgeSources, currentIndex, setCurrentIndex, handleMultiSelection } =
    useKnowledgeSourceSelection()

  const navigate = useNavigate()

  const hasUnresolvedPages = hasNextPage && !isFetching
  const MAX_PAGES = 5
  const pages = data.pages.flatMap((page) => page.files)

  useEffect(() => {
    if (hasNextPage && data.pages.length < MAX_PAGES) {
      void fetchNextPage()
    }
  }, [data.pages.length, fetchNextPage, hasNextPage])

  const updatedAt =
    pages
      .map((page) => page.updatedAt)
      .sort()
      .at(-1) ?? ''
  const size = pages.reduce((acc, page) => acc + (page.size ?? 0), 0)

  // Use the first valid favicon url if available
  const faviconUrl = pages.find((page) => page.metadata?.['favicon'] != null)?.metadata?.['favicon']

  const getStatus = () => {
    for (const page of pages) {
      if (page.status.includes('pending')) {
        return 'indexing_pending'
      }
      if (page.status.includes('failed')) {
        return 'indexing_failed'
      }
      if (page.status.includes('completed')) {
        return 'indexing_completed'
      }
    }
    return 'unknown'
  }

  const crawlTask = useWebSourceCrawl({ workspaceId, botId, kbId, dsId })

  return (
    <TableRow
      className={cn(index === currentIndex && 'bg-muted', 'group h-16 cursor-pointer')}
      onClick={() =>
        navigate({
          to: '/workspaces/$workspaceId/bots/$botId/knowledge-bases/$kbId/$dsId',
          params: { workspaceId, botId, kbId: backendKbId ?? kbId, dsId },
        })
      }
    >
      <TableCell>
        <div className="flex items-center justify-center">
          <Checkbox
            className={cn(
              !selectedKnowledgeSources.webPageSource.includes(dsId) && 'opacity-0 group-hover:opacity-100'
            )}
            onClick={(e) => {
              if (e.shiftKey) {
                e.preventDefault()
                handleMultiSelection(index)
              }
              e.stopPropagation()
            }}
            checked={selectedKnowledgeSources.webPageSource.includes(dsId)}
            onCheckedChange={(checkedState) => {
              if (checkedState) {
                if (currentIndex === -1) {
                  setCurrentIndex(index)
                }
                setSelectedKnowledgeSources({
                  ...selectedKnowledgeSources,
                  webPageSource: [...selectedKnowledgeSources.webPageSource, dsId],
                })
              } else {
                setCurrentIndex(-1)
                setSelectedKnowledgeSources({
                  ...selectedKnowledgeSources,
                  webPageSource: selectedKnowledgeSources.webPageSource.filter((id: string) => id !== dsId),
                })
              }
            }}
          />
        </div>
      </TableCell>
      <TableCell className="max-w-52">
        <div className="flex items-center gap-2">
          {faviconUrl ? (
            <img src={faviconUrl} alt="Favicon" className="size-4" />
          ) : (
            <KnowledgeSourceIcon dsType="web-page" />
          )}
          <p className="truncate">{title}</p>
          <Badge variant="outline" color="blue" className="flex items-center gap-2 whitespace-nowrap">
            {isFetching && !crawlTask && <Loader className="animate-[spin_2s_linear_infinite]" size={16} />}
            <p>
              {pages.length ?? 0}
              {hasUnresolvedPages && '+'} {pages.length === 1 ? 'page' : 'pages'}
            </p>
          </Badge>
        </div>
      </TableCell>
      <TableCell>
        <KnowledgeSourceOrigin dsType="web-page" />
      </TableCell>
      <TableCell>{DateTime.fromISO(updatedAt).toRelative()}</TableCell>
      <TableCell>
        {formatters.byte(size)}
        {hasUnresolvedPages && '+'}
      </TableCell>
      <TableCell>
        {isFetching && hasNextPage ? (
          <div className="flex items-center gap-2">
            <Spinner size="2" />
            <p>Fetching pages...</p>
          </div>
        ) : crawlTask ? (
          <KnowledgeSourceCrawlStatus status={crawlTask.status ?? 'pending'} />
        ) : (
          <KnowledgeSourceStatus status={getStatus() as KnowledgeSourceStatusType} />
        )}
      </TableCell>
    </TableRow>
  )
}
